<template>
  <Component
    :is="nolink ? 'div' : 'nuxt-link'"
    :to="link"
    class="card"
    :class="{ dark, align, completed: !!track.certificateNumber, lms: $config.project === 'lms' }"
    :data-event="`${track.slug.replace(/-/g, '_')}_track.click`"
  >
    <div>
      <div class="image-holder">
        <img
          loading="lazy" class="image" :src="require(`../assets/images/career-track-${track.id}${wide ? '-wide' : ''}.png`)"
          :alt="track.name"
        >
      </div>
      <h3 class="h5">
        <span>{{ track.name }}</span>
        <span v-if="$config.project === 'lms'" class="track">
          Career track
        </span>
        <span v-if="badgeText && $store.state.layout !== 'mobile'" class="badge badge-lg" :class="`badge-${badgeText.toLowerCase()}`">
          {{ badgeText }}
        </span>
      </h3>
      <p class="body-2">
        {{ track.description }}
      </p>
    </div>
    <div>
      <template v-if="$config.project === 'lms' && track.enrolled">
        <Progress
          :progress="track.progress"
          :data="{ completed: track.completedCourses, total: track.totalCourses }"
          text="courses"
          offset
          :certificate="!!track.certificateNumber"
          class="info"
        />
      </template>
      <template v-else>
        <p class="courses" :class="{ h6: $config.project === 'site' }">
          <MonitorIcon />
          <span>{{ track.totalCourses }} courses</span>
        </p>
        <p v-if="certificate" class="certificate">
          <Medal />
          <span>{{ certificateText }}</span>
        </p>
      </template>
      <ButtonArrow
        text="Get Started" type="span"
        :no-animation="true"
        class="button-arrow h5 accent"
      />
    </div>
  </Component>
</template>

<script>
import ButtonArrow from '../components/ButtonArrow';
import Progress from '../Courses/Progress';
import MonitorIcon from '../assets/svg/courses/monitor.svg?vue-component';
import Medal from '../assets/svg/medal.svg?vue-component';

export default {
  components: {
    ButtonArrow, Progress, MonitorIcon, Medal,
  },
  props: {
    track: { type: Object, default: () => {}, required: true },
    certificate: { type: Boolean },
    certificateText: { type: String, default: 'Certificate of Achievement' },
    wide: { type: Boolean },
    dark: { type: Boolean },
    align: { type: Boolean },
    nolink: { type: Boolean },
  },
  computed: {
    badgeText() {
      let t = '';
      if (this.track.completed) {
        t = 'Completed';
      } else if (!this.track.completed && this.track.enrolled) {
        t = 'Enrolled';
      }

      return t;
    },
    link() {
      let link = '/career-tracks/';

      if (this.$route.name.startsWith('business') && this.$config.project === 'lms') {
        link = '/business/library/';
      }

      return `${link}${this.track.slug}/`;
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  display: block;
  position: relative;
  padding: var(--spacing-36);
  background-color: var(--main-light-shade-2);
  border-radius: 10px;
  color: var(--main-dark-shade-0);

  &.align {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }

  &.dark {
    background-color: var(--main-dark-shade-1);
    color: var(--main-light);

    .h5 {
      color: var(--main-light);
    }

    .body-2 {
      color: var(--main-light-shade-4);
    }

    &:after {
      background: linear-gradient(var(--main-dark) 0%, rgba(0, 12, 31, 0.9) 52.6%, rgba(0, 12, 31, 0) 100%);
    }

    &:hover {
      .btn-arrow {
        color: var(--accent);
      }
    }
  }

  &.lms {
    background-color: var(--main-light);
    text-decoration: none;
    border: 1px solid var(--main-light-shade-2);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

    .badge {
      font-size: 14px;
      line-height: 14px;
      padding: 2px 9px;
      font-weight: 500;
      border-radius: 4px;

      &.badge-lg {
        padding: 7px 19px;
      }

      &.badge-completed {
        background-color: rgba(188, 255, 203, 0.2);
        color: var(--main-green);
      }

      &.badge-enrolled {
        background-color: rgba(rgb(86, 191, 199), 0.2);
        color: var(--accent);
      }
    }

    .h5 .track {
      font-weight: 400;
      color: #75818F;
    }

    .body-2 {
      color: var(--main-dark-light-4);
    }

    .info {
      margin-top: 56px;
    }

    .courses {
      &:not(.h6) {
        margin-top: var(--spacing-20);
      }

      + .certificate {
        margin-top: var(--spacing-8);
      }
    }

    .certificate svg {
      path[fill] {
        fill: var(--accent);
      }

      path[stroke] {
        stroke: var(--accent);
      }
    }
  }

  &.completed {
    .image {
      opacity: 0.5;
    }
  }

  .h5 {
    margin: 0;
    display: flex;
    align-items: center;

    .track {
      margin-left: 5px;
    }

    .badge {
      margin-left: 10px;
    }
  }

  .body-2 {
    color: var(--main-dark-shade-3);
    margin-top: var(--spacing-12);
    margin-bottom: var(--spacing-24);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    -webkit-box-orient: vertical;
  }

  .certificate {
    display: flex;
    align-items: center;
    column-gap: 8px;
    margin: 0;

    svg {
      width: 22px;
      height: 22px;

      path[fill] {
        fill: var(--main-dark-shade-0);
      }

      path[stroke] {
        stroke: var(--main-dark-shade-0);
      }
    }

    span {
      font-size: 16px;
    }
  }

  .courses {
    margin-bottom: var(--spacing-8);
    display: flex;
    align-items: center;
    column-gap: 8px;

    svg {
      width: 22px;
      height: 22px;

      path[fill] {
        fill: var(--main-dark-shade-0);
      }

      path[stroke] {
        stroke: var(--main-dark-shade-0);
      }
    }

    span {
      font-weight: 400;
      font-size: 16px;
    }
  }

  .image-holder {
    margin-bottom: var(--spacing-40);

    .image {
      display: block;
      max-width: 100%;
    }
  }

  .button-arrow {
    transition: all var(--duration) ease-in;
    visibility: hidden;
    opacity: 0;
    z-index: 2;
    position: absolute;
    left: 0;
    bottom: var(--spacing-36);
    line-height: 100%;
  }

  &:after {
    transition: all var(--duration) ease-in;
    opacity: 0;
    z-index: -1;
    visibility: hidden;
    content: '';
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(180deg, #F0F0F2 0%, rgba(240, 240, 242, 0.9) 52.6%, rgba(240, 240, 242, 0) 100%);
    border-radius: 10px 10px 0 0;
    transform: rotate(-180deg);
  }

  &.lms:after {
    background: linear-gradient(180deg, #FFF 0%, rgba(255, 255, 255, 0.9) 52.6%, rgba(240, 240, 242, 0) 100%);
  }

  &:hover:not(.disabled) {
    .button-arrow {
      visibility: visible;
      opacity: 1;
      left: var(--spacing-36);
    }

    &:after {
      visibility: visible;
      opacity: 1;
      z-index: 1;
    }
  }
}

@include bp($bp-laptop) {
  .card.align {
    padding: var(--spacing-36) var(--spacing-24);
  }
}

@include bp($bp-mobile) {
  .card {
    height: 100%;
    padding: var(--spacing-36) var(--spacing-32);
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .h5 {
      display: block;
    }

    &.lms {
      .info {
        margin-top: var(--spacing-18);
      }
    }

    &.completed {
      .image {
        opacity: 1;
      }
    }
  }
}
</style>
